import { useApplicantAPI, useRequest } from "@certn/api";
import { Stack, Text } from "@certn/cascadia";
import {
  Address,
  isAddress,
} from "@certn/cascadia/patterns/address-form/types";
import {
  Container,
  Divider,
  Icon,
  ExpandableCard,
  MediaQuery,
} from "@certn/cascadia-react";
import {
  doesCardHaveIcon,
  getPaymentIcon,
  RegionContext,
  useTimeTracking,
} from "@certn/utils";
import { css } from "@emotion/css";
import { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { BillingAddress } from "../../components/billing";
import { PaymentProfileList } from "../../components/payment-profile";
import { PaymentSummaryOrderSummary } from "../../components/order-summary/PaymentSummaryOrderSummary";
import { useApplication } from "@certn/application";
import { useProfile } from "../../auth/ProfileProvider";

export function PaymentSummary() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { caseId } = useParams<{ caseId: string }>();
  const request = useRequest();
  const region = useContext(RegionContext);
  const { stopTimeTracker } = useTimeTracking();
  const { refresh } = useApplication();
  const { profile } = useProfile();

  // Fetch payment summary data
  const {
    data: summaryData,
    isLoading,
    mutate,
  } = useApplicantAPI(
    `/api/internal/applicant/case/${caseId}/payment-summary/`
  );

  // Fetch case data to access applicant_form_data for submission
  const { data: caseData, isLoading: isLoadingCaseData } = useApplicantAPI(
    `/api/internal/applicant/case/${caseId}/`
  );

  const [isPaymentExpanded, setIsPaymentExpanded] = useState(false);
  // Track whether billing address form has unsaved changes
  const [hasBillingAddressChanges, setHasBillingAddressChanges] =
    useState(false);

  const onPaymentToggle = useCallback(
    () => setIsPaymentExpanded((prev) => !prev),
    []
  );

  const onPaymentSave = useCallback(async () => {
    // Collapse the payment section after profile is added
    setIsPaymentExpanded(false);
    // Refresh payment summary data (payment profile existence determines completion status)
    // PaymentProfileList component handles refreshing its own payment profiles data
    await mutate();
    // Refresh case data to update sidebar
    await refresh();
  }, [mutate, refresh]);

  // Auto-expand payment section if no payment profile exists
  useEffect(() => {
    if (!summaryData?.payment_profile) {
      setIsPaymentExpanded(true);
    }
  }, [summaryData?.payment_profile]);

  // Handle Stripe redirect completion (e.g., 3D Secure authentication)
  // After a 3DS redirect we cannot rely on a fixed 1-second timeout: the
  // setup_intent.succeeded webhook that creates the PaymentProfile row in the
  // DB can take longer than 1 second to arrive and be processed. A single
  // mutate() fired too early returns payment_profile: null, leaving the Pay
  // button permanently disabled with no retry.
  // Fix: poll the payment-summary endpoint until the profile appears (mirrors
  // the strategy StripeSetupIntentForm uses for the non-redirect path).
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const redirectStatus = urlParams.get("redirect_status");

    if (redirectStatus !== "succeeded") return;

    let cancelled = false;

    const pollForPaymentProfile = async () => {
      const startTime = Date.now();
      const timeout = 30000; // 30 s — matches StripeSetupIntentForm
      const pollInterval = 2000; // 2 s between retries

      await new Promise((resolve) => setTimeout(resolve, 2000));

      while (!cancelled && Date.now() - startTime < timeout) {
        const freshData = await mutate();
        if (freshData?.payment_profile) {
          setIsPaymentExpanded(false);
          await refresh();
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, pollInterval));
      }

      // Timed out — do a final refresh so the UI is not permanently stuck
      if (!cancelled) {
        setIsPaymentExpanded(false);
        await mutate();
        await refresh();
      }
    };

    pollForPaymentProfile();

    return () => {
      cancelled = true;
    };
  }, [caseId, mutate, refresh]);

  const handleSubmit = async () => {
    // PaymentSummary has its own submit pathway - directly submit the case
    // This is different from application.tsx which redirects applicant-to-pay cases
    try {
      await request(`/api/internal/applicant/case/${caseId}/submit/`, {
        method: "POST",
        body: JSON.stringify(caseData?.applicant_form_data),
      });
      stopTimeTracker("application--submit");
      navigate(`/apply/${caseId}/submitted`);
    } catch (error) {
      throw error;
    }
  };

  if (isLoading || isLoadingCaseData) {
    return (
      <Container>
        <Text style="form/page-title">{t("Loading...")}</Text>
      </Container>
    );
  }

  if (!summaryData || !caseData) {
    return (
      <Container>
        <Text style="form/page-title">
          {t("Error loading payment summary")}
        </Text>
      </Container>
    );
  }

  const paymentProfile = summaryData.payment_profile;
  const hasPaymentProfile = !!paymentProfile;
  const currency = summaryData.currency;

  // Extract current case address from residences (same logic as backend get_current_address_from_residences)
  const residences = caseData?.applicant_form_data?.residences;
  const currentResidence = Array.isArray(residences)
    ? residences.find(
        (residence: { end_date?: string | null }) => residence.end_date == null
      )
    : undefined;
  const caseAddress =
    currentResidence?.address && isAddress(currentResidence.address)
      ? (currentResidence.address as Address)
      : undefined;

  // Determine default address for payment profile: prioritize billingAddress, then caseAddress
  const billingAddress = profile?.billing_address;
  const addressInfo = billingAddress || caseAddress;

  return (
    <Container layout="wide" testID="payment-summary-container">
      <Text as="h1" className={styles.titleSpacing} style="global/title-small">
        {t("Payment details")}
      </Text>

      <div className={styles.mainContentContainer}>
        <Stack className={styles.summary} gap="space-6">
          <Text style="global/body-secondary" color="text-weak">
            {t(
              "Please provide your card details. You will be charged after all the checks in the application have been completed. After the payment has been successful, we will email you a receipt for the checks which have been completed."
            )}
          </Text>

          {/* Billing Address and Payment Method Section */}
          <Stack gap="space-6">
            <BillingAddress
              caseAddress={caseAddress}
              onBillingFormSaveButtonEnabledChange={setHasBillingAddressChanges}
            />
            <MediaQuery sm md>
              <Divider />
            </MediaQuery>
            {/* Payment Method Section */}
            <ExpandableCard
              title={t("Payment method")}
              expanded={isPaymentExpanded}
              iconLabel={
                paymentProfile && !isPaymentExpanded ? t("Change") : undefined
              }
              onToggle={onPaymentToggle}
              subtitle={
                paymentProfile && (
                  <Stack orientation="horizontal" gap="space-2">
                    {paymentProfile.brand &&
                    doesCardHaveIcon(paymentProfile.brand) ? (
                      <img
                        src={
                          paymentProfile.brand
                            ? getPaymentIcon(paymentProfile.brand)
                            : ""
                        }
                        alt={paymentProfile.brand || undefined}
                        width="24px"
                        height="24px"
                      />
                    ) : (
                      <Icon name="credit_card" size="space-6" />
                    )}

                    <Text style="global/body-secondary">
                      {t("Card ending in •••• {{number}}", {
                        number: paymentProfile.number,
                      })}
                    </Text>
                  </Stack>
                )
              }
            >
              <PaymentProfileList
                onPaymentProfileSelected={onPaymentSave}
                defaultAddress={addressInfo}
              />
            </ExpandableCard>
          </Stack>
        </Stack>
        {/* Order Summary */}
        <Stack className={styles.orderSummaryContainer}>
          <PaymentSummaryOrderSummary
            checks={summaryData.checks}
            totalEstimatedCost={summaryData.total_estimated_cost}
            currency={currency}
            disableSubmitButton={!hasPaymentProfile || hasBillingAddressChanges}
            onSubmit={handleSubmit}
            region={region}
          />
        </Stack>
      </div>
    </Container>
  );
}

const styles = {
  titleSpacing: css({
    paddingTop: "var(--space-6)",
  }),
  mainContentContainer: css({
    display: "flex",
    gap: "var(--space-6)",
    flexWrap: "wrap",
  }),
  summary: css({
    flexBasis: "608px",
    flexGrow: 1,
  }),
  orderSummaryContainer: css({
    flexBasis: "304px",
    flexGrow: 1,
  }),
};
